import { getProviderConfig } from './oauthConfig';

/**
 * Funciones auxiliares para PKCE (genéricas para todos los proveedores)
 */

/**
 * Obtener lista de proveedores disponibles (solo IKOS)
 */
export const getAvailableProviders = () => {
  return [
    {
      id: 'ikos',
      name: 'IKOS',
      description: 'Plataforma IoT agrícola',
      icon: '🌱',
      color: '#4CAF50'
    }
  ];
};


export const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashed = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hashed)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

export const generateRandomString = (length = 32) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Procesar parámetros iniciales de cualquier proveedor
 */
export const processProviderRedirect = (searchParams, providerId) => {
  const config = getProviderConfig(providerId); // Ya valida que el proveedor exista
  
  // PRIMERO: Verificar si vienen parámetros estándar de OAuth (callback de autorización)
  const code = searchParams.get('code');
  const error = searchParams.get('error');
  const state = searchParams.get('state');
  
  if (error) {
    throw new Error(`Error de ${config.name}: ${error}`);
  }
  
  // Si hay code y state, es un callback de OAuth (cualquier proveedor)
  if (code && state) {
    return {
      code,
      state,
      error,
      scope: searchParams.get('scope'),
      tokenType: searchParams.get('token_type')
    };
  }
  
  // SEGUNDO: Si no hay callback OAuth, buscar parámetros específicos del proveedor
  if (providerId === 'ikos') {
    const paramP = searchParams.get('p');
    if (!paramP) {
      throw new Error('Parámetro p no encontrado para IKOS');
    }
    
    try {
      const decodedData = JSON.parse(atob(paramP));
      return {
        jwt: decodedData.j,
        email: decodedData.e,
        name: decodedData.n,
        scopes: decodedData.s,
        rawParam: paramP
      };
    } catch (error) {
      throw new Error('Error decodificando parámetro p de IKOS: ' + error.message);
    }
  }
  
  // Para otros proveedores sin callback OAuth
  return {
    code,
    state,
    error,
    scope: searchParams.get('scope'),
    tokenType: searchParams.get('token_type')
  };
};

/**
 * Construir URL de autorización OAuth para cualquier proveedor
 */
export const buildAuthorizationUrl = async (providerId, providerData = {}) => {
  const config = getProviderConfig(providerId); // Ya valida que el proveedor exista
  // Usar la URI de redirección configurada o generar una por defecto
  const redirectUri = config.redirectUri || `${window.location.origin}/oauth/provider/${providerId}`;
  
  let params = {
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri,
    scope: config.scopes.join(' ')
  };
  
  // Agregar PKCE si el proveedor lo soporta
  if (config.usesPKCE) {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);
    
    params.code_challenge = codeChallenge;
    params.code_challenge_method = 'S256';
    params.state = state;
    
    // Guardar para validación posterior
    sessionStorage.setItem(`${providerId}_code_verifier`, codeVerifier);
    sessionStorage.setItem(`${providerId}_oauth_state`, state);
  }
  
  // Agregar parámetros específicos del proveedor
  if (config.customParams) {
    // Para IKOS
    if (providerId === 'ikos' && config.customParams.ikos_jwt && providerData.jwt) {
      params.ikos_jwt = providerData.jwt;
    }
    
    // Para otros proveedores específicos
    if (providerData.customScopes) {
      params.scope = providerData.customScopes;
    }
  }
  
  const urlParams = new URLSearchParams(params);
  return `${config.authorizeUrl}?${urlParams.toString()}`;
};

/**
 * Preparar datos para enviar al backend para intercambio de tokens
 */
export const prepareTokenExchangeData = (providerId, code, state) => {
  const config = getProviderConfig(providerId); // Ya valida que el proveedor exista
  
  let data = {
    provider: providerId,
    code: code
  };
  
  // Agregar validaciones PKCE si el proveedor las usa
  if (config.usesPKCE) {
    const savedCodeVerifier = sessionStorage.getItem(`${providerId}_code_verifier`);
    const savedState = sessionStorage.getItem(`${providerId}_oauth_state`);
    
    if (state !== savedState) {
      throw new Error(`Estado OAuth inválido para ${config.name}`);
    }
    
    if (!savedCodeVerifier) {
      throw new Error(`Code verifier no encontrado para ${config.name}`);
    }
    
    data.code_verifier = savedCodeVerifier;
    data.state = state;
  }
  
  return data;
};

/**
 * Limpiar datos temporales de OAuth
 */
export const cleanupOAuthData = (providerId) => {
  sessionStorage.removeItem(`${providerId}_code_verifier`);
  sessionStorage.removeItem(`${providerId}_oauth_state`);
  sessionStorage.removeItem(`${providerId}_oauth_data`);
};